/*
* Coordinator jobs search for authenticated users
*/

import '@progress/kendo-ui';

import { Grid } from '@progress/kendo-grid-react-wrapper';
import * as _ from 'lodash';
import moment from 'moment';
import * as React from 'react';
import { Button, ButtonToolbar, Modal, Row, Well } from 'react-bootstrap';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { push } from 'react-router-redux';
import VirtualizedSelect from 'react-virtualized-select';
import { compose } from 'redux';
import { connectRequest } from 'redux-query';

import * as Actions from '../actions';
import * as MutateActions from '../actions/mutate';
import * as AppComp from '../common/app-component-base';
import * as AppUtilities from '../common/apputilities';
import { BootstrapColXl as Col } from '../common/bootstrap/bootstrapcolxl';
import { Formatters } from '../common/formatters';
import { FormUtilities } from '../common/formutilities';
import { GridUtilities } from '../common/grid/gridutilities';
import { NavigationLinks } from '../common/navigation';
import { WhiteBoxPageSection } from '../common/whiteboxpagesection';
import ApiEndpoints from '../config/apiendpoints';
import AppConfiguration from '../config/configuration';
import * as ReduxQueries from '../queries';

export interface ICoordinatorJobsSearchProps extends AppComp.IAppComponentProps {
    filterByRegions: any;
    filterByStatuses: any;
    filterByUsers: any;
    updateOrdersSetAsPaid?: any;
    forceRequest?: any;
    dispatch?: any;
    navigateTo?: any;
}

export interface ICoordinatorJobsSearchState extends AppComp.IAppComponentState {
    selectedKeys?: any;
    formChanges?: any;
    showModalConfirmSetAllPaid?: boolean;
}

class CoordinatorJobsSearch extends AppComp.AppComponent<ICoordinatorJobsSearchProps, ICoordinatorJobsSearchState> {
    public MomentDateFormat: string = AppConfiguration.getMomentShortDateFormat();
    public orderObjIds: Array<string> = _.stubArray();
    public gridDataSource: any;
    public kendoGridWidgetRef: any;

    public gridOptions = GridUtilities.kendoGridOptionsPaging(
        [
            GridUtilities.kendoGridColumnDefinition('', '', 30, 100, undefined, { selectable: true }),
            GridUtilities.kendoGridColumnDefinition('associationId', this.appTextGridColumnHeader('association-id'), 50, 1),
            GridUtilities.kendoGridColumnDefinition('associationName', this.appTextGridColumnHeader('association-name'), 180, 2),
            GridUtilities.kendoGridColumnDefinition('acceptedPrice', this.appTextGridColumnHeader('accepted-price'), 80, 3, undefined, { format: '{0:c}' }),
            GridUtilities.kendoGridColumnDefinition('contactFirstName', this.appTextGridColumnHeader('contact-first-name'), 80, 4),
            GridUtilities.kendoGridColumnDefinition('contactLastName', this.appTextGridColumnHeader('contact-last-name'), 90, 5),
            GridUtilities.kendoGridColumnDefinition('contactEmail', this.appTextGridColumnHeader('contact-email'), 120, 6),
            GridUtilities.kendoGridColumnDefinition('clientName', this.appTextGridColumnHeader('client-name'), 100, 7),
            GridUtilities.kendoGridColumnDefinition('statusName', this.appTextGridColumnHeader('status-name'), 60, 7),
            GridUtilities.kendoGridColumnDefinition('completionDateFormatted', this.appTextGridColumnHeader('completion-date'), 70, 7),
            GridUtilities.kendoGridColumnDefinition('inspectorPaid', this.appTextGridColumnHeader('inspector-paid'), 60, 8),
            GridUtilities.kendoGridColumnDefinition('regionName', this.appTextGridColumnHeader('region-name'), 70, 9),
            GridUtilities.kendoGridColumnDefinition('viewDetailsLink', '', 60, 12,
                                                    GridUtilities.kendoGridLinkColumnTemplate('viewDetailsLink', 'viewDetailsLinkHref', this.appText('view-details-link-title')),
                                                    { headerTemplate: '' }),
        ],
        this.appText('no-results'),
        {
            persistSelection: true,
            widgetRef: (w) => { this.kendoGridWidgetRef = w; },
            change: (e) => {
                const selectedKeys = e.sender.selectedKeyNames();
                this.handleGridRowCheckbox(selectedKeys);
            },
        },
        this.appTextPageTitle()
    );

    public constructor(props: ICoordinatorJobsSearchProps) {
        super(props, 'app.coordinator-jobs-search.');

        this.state = {
            formChanges: {},
            showModalConfirmSetAllPaid: false,
            selectedKeys: [],
        };
    }

    public componentDidMount() {
        const enforce = this.enforceAdminOrEmployeeOnly();

        if (enforce) { this.props.navigateTo(NavigationLinks.clientDashboardHome()); }
    }

    public extendGridData(records: Array<any>) {
        // Extend grid row data
        if (!records) { return []; }

        return records.map( r => {
            let record = _.cloneDeep(r);

            record.associationName = _.get(record, 'association.name', '');
            record.contactFirstName = _.get(record, 'association.contact.firstName', '');
            record.contactLastName = _.get(record, 'association.contact.lastName', '');
            record.contactEmail = _.get(record, 'association.contact.email', '');
            record.clientName = _.get(record, 'client.name', '');
            record.statusName = _.get(record, 'status.name', '');
            record.regionName = _.get(record, 'client.region.name', '');

            record.completionDateFormatted = '';
            if (record.completionDate) {
                record.completionDateFormatted = moment(record.completionDate).format(this.MomentDateFormat);
            }

            record.viewDetailsLink = '';
            record.viewDetailsLinkHref = '';
            if (record.orderId) {
                record.viewDetailsLink = this.appText('view-details');
                // record.viewDetailsLinkHref = NavigationLinks.orderEdit(record.orderId);
                record.viewDetailsLinkHref = NavigationLinks.orderDetail(record.orderId);
            }

            return record;
        });
    }

    // HANDLERS
    public handleGridRowCheckbox(keys: any) {
        const nextSelectedKeys = _.cloneDeep(keys);
        const prevSelectedKeys = _.get(this.state, 'selectedKeys', []);
        const same = _.isEqual(nextSelectedKeys, prevSelectedKeys);

        if (!same) { this.setState({ selectedKeys: nextSelectedKeys }); }
    }

    // FROM send-bids.tsx
    // public handleSelectCoordinator(e: any) {
    //     const value = e ? e.value : '';
    //     this.props.sendBidsFilterCoordinator(value);
    // }

    public handleSelectFromDate(e: any) {
        const value = e ? e.format(this.MomentDateFormat) : null;
        this.props.dispatch(Actions.searchCoordinatorJobsFilterFromDate(value));
    }

    public handleSelectToDate(e: any) {
        const value = e ? e.format(this.MomentDateFormat) : null;
        this.props.dispatch(Actions.searchCoordinatorJobsFilterToDate(value));
    }

    public handleSelectCoordinator(e: any) {
        const value = e ? e.value : '';
        this.props.dispatch(Actions.searchCoordinatorJobsFilterCoordinator(value));
    }

    public handleSelectRegion(e: any) {
        const value = e ? e.value : '';
        this.props.dispatch(Actions.searchCoordinatorJobsFilterRegionId(value));
    }

    public handleSelectStatus(e: any) {
        const value = e ? e.value : '';
        this.props.dispatch(Actions.searchCoordinatorJobsFilterStatusId(value));
    }

    public handleSelectPaid(e: any) {
        const value = e ? e.value : '';
        this.props.dispatch(Actions.searchCoordinatorJobsFilterPaid(value));
    }

    public handleShowModalConfirmSetAllPaid(e: any) {
        this.setState({ showModalConfirmSetAllPaid: true });
    }

    public handleCloseModalConfirmSetAllPaid() {
        this.setState({ showModalConfirmSetAllPaid: false });
    }

    public handleConfirmSetAllPaid(e: any) {
        const token = Formatters.getStr(this.props, 'authsession.access_token');

        const selectedKeysOrderObjIds = _.get(this.state, 'selectedKeys', []);

        const changeData = _.merge(
            {},
            { orderObjIds: (_.size(selectedKeysOrderObjIds) > 0) ? selectedKeysOrderObjIds : [] },
        );

        /* console.log(`handleConfirmSetAllPaid selectedKeysOrderObjIds.len ${_.size(selectedKeysOrderObjIds)}`, {
            selectedKeysOrderObjIds,
            changeData,
            st: this.state,
        }); */

        this.props.updateOrdersSetAsPaid(changeData, token, false).then(response => {
            // We can promise-chain on the mutation action to trigger state
            // transitions when it succeeds or fails.

            if (response.status >= 200 && response.status < 300) {
                // De-select checked rows 20200120
                if (this.kendoGridWidgetRef) { this.kendoGridWidgetRef.clearSelection(); }

                this.setState({
                    showModalConfirmSetAllPaid: false,
                    selectedKeys: undefined,
                });
                this.props.forceRequest();
            } else {
              // Error during creation...
            }
        });
    }

    public renderHeaderButtonToolbar = (filterMode: any) => {
        let buttonProps = {
            bsSize: 'sm',
            bsStyle: 'info',
        };
        // console.log(`renderHeaderButtonToolbar filterMode ${filterMode}`);

        return (
            <div>
                <Well className="header-well-buttontoolbar">
                    <Row>
                        <Col xs={3}>
                            <ButtonToolbar>
                                <Button {...buttonProps} onClick={(e) => this.handleShowModalConfirmSetAllPaid(e)}><i className="fa fa-money" /> {this.appText('set-all-paid')}</Button>
                            </ButtonToolbar>
                        </Col>
                    </Row>
                </Well>
            </div>
        );
    }

    public renderModalConfirmSetAllPaid() {
        return (
            <Modal enforceFocus={false} show={this.state.showModalConfirmSetAllPaid} onHide={() => this.handleCloseModalConfirmSetAllPaid()}>
                <Modal.Header closeButton={true}>
                    <Modal.Title>{this.appText('modals.confirm-set-all-paid.title')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {this.appText('modals.confirm-set-all-paid.body')}
                </Modal.Body>
                <Modal.Footer>
                    <Button bsStyle="success" onClick={(e) => { this.handleConfirmSetAllPaid(e); }}>{this.appText('modals.confirm-set-all-paid.success')}</Button>
                    <Button bsStyle="danger" onClick={() => this.handleCloseModalConfirmSetAllPaid()}>{this.appTextCommon('buttons.cancel')}</Button>
                </Modal.Footer>
            </Modal>
        );
    }

    public render() {
        const { search, filterByUsers, filterByRegions, filterByStatuses, ...rest } = this.props;

        const rows = this.extendGridData(search.results);
        this.gridDataSource = GridUtilities.kendoGridDataSourcePaged(rows, {
            schema: {
                model: {
                    id: '_id',
                }
            }
        });

        // Prepare select dropdown options
        let extendedUsers = [];
        if (AppUtilities.isArrayWithLength(filterByUsers)) {
            extendedUsers = this.props.filterByUsers.map(c => {
                c.sort = Formatters.getStr(c, 'contact.lastName').trim().toLowerCase();
                if (!c.label) { c.label = Formatters.composeUsernameLabel(c, true); }

                return c;
            });
        }
        const filterOptionAny = this.appTextFilterOptionAny();
        const users = FormUtilities.getExtendedFilterOptions(extendedUsers, 'username', 'sort', filterOptionAny, '', '_');
        // const regions = FormUtilities.getExtendedFilterOptions(filterByRegions, 'regionId', 'label', filterOptionAny, -1);
        // const statuses = FormUtilities.getExtendedFilterOptions(filterByStatuses, 'statusId', 'statusId', filterOptionAny, -1);

        // const paidOptionsExtended = [
        //     {value: -1, label: filterOptionAny},
        //     {value: 0, label: this.appText('filter-option-paid-no')},
        //     {value: 1, label: this.appText('filter-option-paid-yes')},
        // ];
        // const paidOptions = AppUtilities.isArrayWithLength(paidOptionsExtended) ? FormUtilities.buildSelectOptions(paidOptionsExtended, 'label', 'label', 'value') : [];

        // Grid buttons
        let gridHeaderBulkActionButtonsDisabled = (_.size(_.get(this.state, 'selectedKeys', [])) > 0) ? false : true;
        let gridHeaderBulkActionButtonsProps = {
            bsSize: 'xs',
            bsStyle: 'info',
        };
        let gridHeaderBulkActionButtons = (
            <div className="send-bids-grid-toolbar-wrapper">
                <Row>
                    <Col xs={12}>
                        <ButtonToolbar className="association-inventory-components-grid-toolbar">
                            <Button
                                {...gridHeaderBulkActionButtonsProps}
                                disabled={gridHeaderBulkActionButtonsDisabled}
                                onClick={(e) => this.handleShowModalConfirmSetAllPaid(e)}
                            >
                                <i className="fa fa-money" /> {this.appText('set-all-paid')}
                            </Button>
                        </ButtonToolbar>
                    </Col>
                </Row>
            </div>
        );

        // Search filters
        const searchFilters = search.filters;

        /*
        let fromDateSelected: any = null;
        if (searchFilters.fromDate) {
            fromDateSelected = moment(searchFilters.fromDate, this.MomentDateFormat);
        }

        let toDateSelected: any = null;
        if (searchFilters.toDate) {
            toDateSelected = moment(searchFilters.toDate, this.MomentDateFormat);
        } */

        const pageTitle = this.appTextResultsAndPageTitle(rows.length);
        const page = (
            <div>
                <div>
                    { /* this.renderHeaderButtonToolbar(null) */ }
                    {this.renderModalConfirmSetAllPaid()}
                </div>

                <WhiteBoxPageSection title={pageTitle}>
                    <Row>
                        <Col xs={12}>
                            <form onSubmit={e => { e.preventDefault(); }}>
                                <Row className="form-group">
                                    <Col xs={12} lg={4}>
                                        <label className="control-label">{this.appText('filter-by-coordinator')}</label>
                                        <VirtualizedSelect
                                            clearable={false}
                                            filterOptions={users.filter}
                                            name="user-filter"
                                            onChange={e => this.handleSelectCoordinator(e)}
                                            options={users.options}
                                            // value={searchFilters.coordinator}
                                            value={search.filters.coordinator}
                                        />
                                    </Col>
                                </Row>
                            </form>
                        </Col>
                    </Row>

                    {gridHeaderBulkActionButtons}

                    <Row>
                        <Col xs={12}>
                            <Grid dataSource={this.gridDataSource} {...this.gridOptions} />
                        </Col>
                    </Row>
                </WhiteBoxPageSection>
            </div>
        );

        return (
            <AppComp.RenderPageWithLayout branding={this.props.branding} pageName={pageTitle} {...rest}>
                {page}
            </AppComp.RenderPageWithLayout>
        );
    }
}

const getApiUrl = () => ApiEndpoints.getCoordinatorJobsSearchUrl();

const mapPropsToConfigs = (props) => ReduxQueries.Queries.searchCoordinatorJobsRequest(
    getApiUrl(),
    ReduxQueries.PropsAccessToken(props),
    _.get(props, 'search.filters.fromDate', ''),
    _.get(props, 'search.filters.coordinator', ''),
    _.get(props, 'search.filters.statusId', -1),
    _.get(props, 'search.filters.toDate', ''),
    _.get(props, 'search.filters.regionId', -1),
    _.get(props, 'search.filters.paid', -1),
);

const mapStateToProps = (state, props) => {
    return ReduxQueries.MapQueryStateToPropsByUrl(getApiUrl(), state, props, {
        filterByUsers: state.entities.searchCoordinatorJobsUsers,
        filterByRegions: state.entities.searchCoordinatorJobsRegions,
        filterByStatuses: state.entities.searchCoordinatorJobsStatuses,
        search: {
            results: state.entities.searchCoordinatorJobsResults,
            filters: {
                fromDate: state.searchFiltersCoordinatorJobs.fromDate,
                coordinator: state.searchFiltersCoordinatorJobs.coordinator,
                statusId: state.searchFiltersCoordinatorJobs.statusId,
                toDate: state.searchFiltersCoordinatorJobs.toDate,
                regionId: state.searchFiltersCoordinatorJobs.regionId,
                paid: state.searchFiltersCoordinatorJobs.paid,
            }
        },
    });
};

const mapDispatchToProps = dispatch => ({
    dispatch: (d) => dispatch(d),
    updateOrdersSetAsPaid: (a: any, b: string, c: boolean) => dispatch(MutateActions.updateOrdersSetAsPaidMutateAsync(a, b, c)),
    navigateTo: (path) => dispatch(push(path)),
});

const CoordinatorJobsSearchContainer = compose<any>(
    connect(mapStateToProps, mapDispatchToProps),
    connectRequest(mapPropsToConfigs)
  )(CoordinatorJobsSearch);

export default withRouter(CoordinatorJobsSearchContainer);



// WEBPACK FOOTER //
// ./src/coordinator-jobs-search/index.tsx